import { TextFieldProps } from "@mui/material";
import { UiButtonBase, UiTextFieldV2 } from "@smp/ui";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { clsx } from "clsx";
import { FC, useCallback, useMemo, useRef } from "react";

import classes from "./form_number_text_field.module.css";

export type IFormNumberTextFieldProps = Omit<
  TextFieldProps,
  "type" | "variant" | "contentEditable" | "validationEnabled" | "value"
> & {
  value: number;
};

export const FormNumberTextField: FC<IFormNumberTextFieldProps> = (props) => {
  const inputRef = useRef<HTMLInputElement>();

  const valueInt = useMemo(
    () => (Number.isNaN(Number(props.value)) ? 0 : Number(props.value)),
    [props.value],
  );

  const min = useMemo<number>(
    () => props.inputProps?.min ?? -Infinity,
    [props.inputProps?.min],
  );

  const max = useMemo<number>(
    () => props.inputProps?.max ?? Infinity,
    [props.inputProps?.max],
  );

  const setValue = useCallback(
    (newValue: number) => {
      if (!inputRef.current) {
        return;
      }

      const newValidValue = Math.max(min, Math.min(newValue, max));

      const valueDescriptor = Object.getOwnPropertyDescriptor(
        Object.getPrototypeOf(inputRef.current),
        "value",
      );

      if (valueDescriptor && valueDescriptor.set) {
        valueDescriptor.set.call(inputRef.current, newValidValue);
      }

      const event = new Event("input", { bubbles: true });
      inputRef.current.dispatchEvent(event);
    },
    [max, min],
  );

  const handleInc = useCallback(() => {
    if (!inputRef.current) {
      return;
    }

    const curValue = Number.isNaN(Number(inputRef.current.value))
      ? 0
      : Number(inputRef.current.value);

    setValue(curValue + 1);
  }, [setValue]);

  const handleDec = useCallback(() => {
    if (!inputRef.current) {
      return;
    }

    const curValue = Number.isNaN(Number(inputRef.current.value))
      ? 0
      : Number(inputRef.current.value);

    setValue(curValue - 1);
  }, [setValue]);

  return (
    <div className={classes.container}>
      <UiButtonBase
        className={classes.btn}
        onClick={handleDec}
        disabled={props.disabled || valueInt <= min}
      >
        <IconMinus size={16} />
      </UiButtonBase>

      <UiTextFieldV2
        variant="outlined"
        type="number"
        contentEditable={false}
        validationEnabled
        {...props}
        className={classes.textField}
        inputProps={{
          ...props.inputProps,
          className: clsx(classes.input, props.inputProps?.className),
        }}
        inputRef={inputRef}
      />

      <UiButtonBase
        className={classes.btn}
        onClick={handleInc}
        disabled={props.disabled || valueInt >= max}
      >
        <IconPlus size={16} />
      </UiButtonBase>
    </div>
  );
};
